import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Droplets, Loader2, MapPin, Printer, UserRound, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatBrazilDate } from '@/lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(value) {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return day && month && year ? `${day}/${month}/${year}` : value;
}

function todayBR() {
  return formatBrazilDate(new Date(), '');
}

function formatDateForFilename(value) {
  return formatDate(value).replaceAll('/', '.');
}

function sanitizeTitlePart(value) {
  return (value || '').toString().trim().replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ');
}

function printDocumentWithTitle(title) {
  const previousTitle = document.title;
  document.title = title;
  setTimeout(() => {
    window.print();
    setTimeout(() => { document.title = previousTitle; }, 500);
  }, 80);
}

export default function BatismoDetailPage() {
  const { batismoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ batismo: null, registros: [], churches: [] });

  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(`${API}/batismos/${batismoId}`, { credentials: 'include' });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.detail || 'Erro ao carregar batismo');
      setData(json);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [batismoId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePrintSheet = () => {
    const title = ['Batismo', formatDateForFilename(data.batismo?.data), sanitizeTitlePart(data.batismo?.atendimento)].filter(Boolean).join(' - ');
    printDocumentWithTitle(title);
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="batismo-detail-loading"><Loader2 className="w-7 h-7 animate-spin text-[#069668]" /></div>;
  const batismo = data.batismo;
  const registrosOrdenados = [...(data.registros || [])].sort((a, b) => `${a.comum || ''}`.localeCompare(`${b.comum || ''}`, 'pt-BR') || `${a.nome || ''}`.localeCompare(`${b.nome || ''}`, 'pt-BR'));
  const ageBuckets = registrosOrdenados.reduce((acc, reg) => {
    const idade = Number(reg.idade);
    if (Number.isNaN(idade)) return acc;
    if (idade <= 12) acc.ate12 += 1;
    else if (idade <= 25) acc.de13a25 += 1;
    else acc.mais25 += 1;
    return acc;
  }, { ate12: 0, de13a25: 0, mais25: 0 });
  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 print:bg-white print:p-0" data-testid="batismo-detail-page">
      <style>{`@page{size:A4 landscape;margin:8mm}@media print{body{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}.no-print{display:none!important}.print-sheet{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important}.print-card{box-shadow:none!important;border:0!important}.print-meta-card{border:1px solid #cbd5e1!important;background:#f8fafc!important}.print-title{font-size:18px!important;line-height:1.1!important}.print-subtitle{font-size:10px!important}.print-footer{display:flex!important}}`}</style>
      <div className="print-sheet max-w-5xl mx-auto space-y-4 bg-white print:space-y-3 sm:p-6 print:p-0 shadow-sm print:shadow-none">
        <div className="no-print flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="batismo-detail-back-button" className="text-slate-600 hover:bg-white"><ArrowLeft className="w-4 h-4 mr-1.5" />Voltar</Button>
          <Button size="sm" onClick={handlePrintSheet} data-testid="batismo-detail-print-sheet-button" className="bg-[#069668] hover:bg-[#057f59]"><Printer className="w-4 h-4 mr-1.5" />Imprimir Tabela</Button>
        </div>
        <header className="border-b-2 border-[#069668] pb-3 print:pb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><Droplets className="w-6 h-6 text-[#069668]" /></div>
              <div>
                <p className="text-xs uppercase font-bold text-[#069668] tracking-normal">Relatório oficial</p>
                <h1 className="print-title text-2xl font-bold text-slate-950" style={{ fontFamily: 'Work Sans, sans-serif' }}>Batismo</h1>
                <p className="print-subtitle text-sm text-slate-600">Emitido em {todayBR()}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Total de batizados</p>
              <p className="text-3xl font-bold text-[#069668]">{data.registros.length}</p>
            </div>
          </div>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-2 print:grid-cols-3" data-testid="batismo-print-meta">
          <div className="print-meta-card rounded-lg bg-slate-50 border border-slate-200 p-3"><p className="text-[11px] uppercase font-bold text-slate-500">Data</p><p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#069668]" />{formatDate(batismo?.data)}</p></div>
          <div className="print-meta-card rounded-lg bg-slate-50 border border-slate-200 p-3"><p className="text-[11px] uppercase font-bold text-slate-500">Atendimento</p><p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5 text-[#069668]" />{batismo?.atendimento}</p></div>
          <div className="print-meta-card rounded-lg bg-slate-50 border border-slate-200 p-3"><p className="text-[11px] uppercase font-bold text-slate-500">Igreja</p><p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#069668]" />{batismo?.igreja_batismo_name || 'Não informado'}</p></div>
        </section>

        <section className="print-meta-card rounded-lg bg-slate-50 border border-slate-200 p-3">
          <p className="text-[11px] uppercase font-bold text-slate-500 mb-2">Igrejas participantes</p>
          <div className="flex flex-wrap gap-1.5" data-testid="batismo-detail-churches">
            {(batismo?.igrejas_participantes || []).map(ch => <Badge key={ch.church_id} variant="outline" className="bg-white text-slate-700 border-slate-300"><Users className="w-3 h-3 mr-1" />{ch.name}</Badge>)}
          </div>
          {batismo?.observacoes && <p className="text-xs text-slate-600 mt-2"><span className="font-semibold">Observações:</span> {batismo.observacoes}</p>}
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-2 print:grid-cols-3" data-testid="batismo-age-summary">
          <div className="print-meta-card rounded-lg bg-white border border-slate-200 p-3" data-testid="batismo-age-card-ate-12">
            <p className="text-[11px] uppercase font-bold text-slate-500">Até 12 anos</p>
            <p className="text-2xl font-bold text-[#069668] leading-tight">{ageBuckets.ate12}</p>
          </div>
          <div className="print-meta-card rounded-lg bg-white border border-slate-200 p-3" data-testid="batismo-age-card-13-25">
            <p className="text-[11px] uppercase font-bold text-slate-500">De 13 a 25 anos</p>
            <p className="text-2xl font-bold text-[#069668] leading-tight">{ageBuckets.de13a25}</p>
          </div>
          <div className="print-meta-card rounded-lg bg-white border border-slate-200 p-3" data-testid="batismo-age-card-mais-25">
            <p className="text-[11px] uppercase font-bold text-slate-500">+ de 25 anos</p>
            <p className="text-2xl font-bold text-[#069668] leading-tight">{ageBuckets.mais25}</p>
          </div>
        </section>

        <Card className="print-card bg-white border-0 shadow-sm overflow-hidden">
          <CardHeader className="px-0 py-2 border-b-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-bold text-slate-950">Batizados</h2>
                <p className="text-xs text-slate-500 print:text-slate-700">Lista compacta para assinatura, conferência e arquivo</p>
              </div>
              <Badge className="bg-emerald-100 text-[#069668] print:bg-white print:text-slate-900 print:border print:border-slate-400" data-testid="batismo-detail-total-batizados">{data.registros.length} registros</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {registrosOrdenados.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="batismo-detail-empty">Nenhum batizado registrado.</div> : (
              <div className="overflow-x-auto" data-testid="batismo-detail-sheet-print"><table className="w-full border-collapse text-sm"><thead><tr className="bg-slate-100 text-slate-800"><th className="text-left px-2 py-2 border border-slate-300 text-[11px] font-bold uppercase">Nome</th><th className="text-left px-2 py-2 border border-slate-300 text-[11px] font-bold uppercase">Idade</th><th className="text-left px-2 py-2 border border-slate-300 text-[11px] font-bold uppercase">Comum</th><th className="text-left px-2 py-2 border border-slate-300 text-[11px] font-bold uppercase">WhatsApp</th><th className="text-left px-2 py-2 border border-slate-300 text-[11px] font-bold uppercase">Endereço digitado</th></tr></thead><tbody>{registrosOrdenados.map(reg => <tr key={reg.registro_id} className="odd:bg-white even:bg-slate-50"><td className="px-2 py-1.5 border border-slate-200 text-xs font-bold text-slate-900" data-testid={`batismo-print-table-nome-${reg.registro_id}`}>{reg.nome}</td><td className="px-2 py-1.5 border border-slate-200 text-xs font-normal text-slate-700" data-testid={`batismo-print-table-idade-${reg.registro_id}`}>{reg.idade} anos</td><td className="px-2 py-1.5 border border-slate-200 text-xs font-normal text-slate-700" data-testid={`batismo-print-table-comum-${reg.registro_id}`}>{reg.comum}</td><td className="px-2 py-1.5 border border-slate-200 text-xs font-normal text-slate-700" data-testid={`batismo-print-table-whatsapp-${reg.registro_id}`}>{reg.whatsapp || 'Sem Telefone'}</td><td className="px-2 py-1.5 border border-slate-200 text-xs font-normal text-slate-700" data-testid={`batismo-print-table-endereco-${reg.registro_id}`}>{reg.endereco_digitado || reg.endereco || '—'}</td></tr>)}</tbody></table></div>
            )}
          </CardContent>
        </Card>

        <Card className="print-card bg-white border-0 shadow-sm overflow-hidden" data-testid="batismo-lgpd-compliance-card">
          <CardHeader className="px-0 py-2 border-b-0">
            <div>
              <h2 className="text-base font-bold text-slate-950">LGPD</h2>
              <p className="text-xs text-slate-500 print:text-slate-700">Nota de Conformidade com a legislação brasileira</p>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="print-meta-card rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-[10px] leading-relaxed text-slate-700">
                Este documento contém dados pessoais comuns (nome, idade, telefone e endereço) estritamente necessários para a finalidade de registro e gestão das atividades institucionais da organização. Informamos que, nos termos do Artigo 5º, incisos I e II da Lei nº 13.709/2018 (Lei Geral de Proteção de Dados - LGPD), as informações aqui listadas não se enquadram na categoria de &quot;dados pessoais sensíveis&quot;, uma vez que não revelam origem racial, convicção religiosa, opinião política, filiação a sindicato, dados de saúde ou genéticos. O tratamento destes dados é realizado com segurança, confidencialidade e em estrito cumprimento às bases legais da referida lei, sendo vedada a sua utilização para fins discriminatórios ou repasse a terceiros sem autorização.
              </p>
            </div>
          </CardContent>
        </Card>

        <footer className="print-footer hidden justify-between gap-4 pt-6 text-[10px] text-slate-500 border-t border-slate-200">
          <span>Relatório de Batismo · {formatDate(batismo?.data)}</span>
          <span>Emitido em {todayBR()}</span>
        </footer>
      </div>
    </div>
  );
}
